import clsx from "clsx";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import CookieSettingsContainer from "web/react/components/cookie-settings-container/cookie-settings-container";
import { SESSION_REPLAY_UNMASK_CLASS } from "web/react/utils/session-replay";
import { openOverlay } from "web/redux/ducks/cookie-settings";
import environment from "web/script/modules/environment";
import { FooterSerializer } from "web/types/serializers";
import FooterBlock from "./footer-block";
import FooterHelpInfo from "./footer-help-info";
import FooterInternationalLinks from "./footer-i18n-links";
import FooterLeadContent from "./footer-lead-content";
import * as styles from "./footer.css";

export interface FooterProps {
    data: FooterSerializer;
    helpCentreURLs: string[];
    showCookieSettingsOption: boolean;
    country: string;
}

type FooterComponentProps = ReactComponentProps & FooterProps;

export interface FooterContextProps extends FooterProps {
    openBlockId: string | null;
    setOpenBlock: (blockId: string) => void;
    openCookieSettingsModal: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

const FooterContext = React.createContext<FooterContextProps | undefined>(undefined);

export function useFooterContext(): FooterContextProps {
    const context = React.useContext(FooterContext);

    if (!context) {
        throw new Error("useFooterContext must be used within a FooterContextProvider");
    }

    return context;
}

function Footer({
    data,
    helpCentreURLs,
    showCookieSettingsOption,
    country,
    children,
}: FooterComponentProps): JSX.Element {
    const dispatch = useDispatch();
    const [openBlockId, setOpenBlockId] = useState<string | null>(null);
    const oneTrustEnabled = environment.getFeature("web_onetrust_enabled");

    function setOpenBlock(blockId: string): void {
        if (blockId === openBlockId) {
            // close everything
            setOpenBlockId(null);
        } else {
            // Set the new active block
            setOpenBlockId(blockId);
        }
    }
    function openCookieSettingsModal(event: React.MouseEvent<HTMLAnchorElement>): void {
        event.preventDefault();
        dispatch(openOverlay());
    }

    return (
        <FooterContext.Provider
            value={{
                data,
                helpCentreURLs,
                showCookieSettingsOption,
                country,
                openBlockId,
                setOpenBlock,
                openCookieSettingsModal,
            }}
        >
            <div className={clsx(styles.footerWrap, SESSION_REPLAY_UNMASK_CLASS)}>
                {!oneTrustEnabled && <CookieSettingsContainer />}
                <footer className={styles.footer}>{children}</footer>
            </div>
        </FooterContext.Provider>
    );
}

Footer.Block = FooterBlock;
Footer.LeadContent = FooterLeadContent;
Footer.InternationalLinks = FooterInternationalLinks;
Footer.HelpInfo = FooterHelpInfo;

export default Footer;
